import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";

interface Stats {
  branches: number;
  activeBranches: number;
  users: number;
  cashiers: number;
  permissions: number;
}

interface AuditRow {
  id: string;
  user_name: string | null;
  action: string;
  entity: string;
  created_at: string;
}

const ACTION_LABELS: Record<string, string> = {
  crear: "Creó",
  editar: "Editó",
  cambiar_pin: "Cambió PIN de",
  eliminar: "Eliminó",
};

const ENTITY_LABELS: Record<string, string> = {
  profiles: "usuario",
  branches: "sucursal",
  customers: "cliente",
  customer_vehicles: "vehículo",
  services: "servicio",
  offers: "oferta",
};

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Buenos días";
  if (hour < 19) return "Buenas tardes";
  return "Buenas noches";
}

export default function AdminDashboardPage() {
  const { profile, roleName } = useAuth();
  const [stats, setStats] = useState<Stats>({
    branches: 0,
    activeBranches: 0,
    users: 0,
    cashiers: 0,
    permissions: 0,
  });
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const [branches, activeBranches, users, cashiers, permissions, auditRes] = await Promise.all([
        supabase.from("branches").select("*", { count: "exact", head: true }),
        supabase.from("branches").select("*", { count: "exact", head: true }).eq("active", true),
        supabase.from("profiles").select("*", { count: "exact", head: true }),
        supabase.from("profiles").select("*", { count: "exact", head: true }).eq("active", true),
        supabase.from("permissions").select("*", { count: "exact", head: true }),
        supabase
          .from("audit_logs")
          .select("id, user_name, action, entity, created_at")
          .order("created_at", { ascending: false })
          .limit(6),
      ]);

      setStats({
        branches: branches.count ?? 0,
        activeBranches: activeBranches.count ?? 0,
        users: users.count ?? 0,
        cashiers: cashiers.count ?? 0,
        permissions: permissions.count ?? 0,
      });
      setAudit((auditRes.data ?? []) as AuditRow[]);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const today = new Date().toLocaleDateString("es-DO", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  const cards = [
    {
      key: "branches",
      label: "Sucursales",
      value: stats.branches,
      hint: `${stats.activeBranches} activas`,
      icon: "ri-building-2-line",
      tone: "primary",
    },
    {
      key: "users",
      label: "Usuarios del sistema",
      value: stats.users,
      hint: `${stats.cashiers} con acceso activo`,
      icon: "ri-team-line",
      tone: "accent",
    },
    {
      key: "permissions",
      label: "Permisos configurables",
      value: stats.permissions,
      hint: "Control de cada cajero",
      icon: "ri-shield-keyhole-line",
      tone: "secondary",
    },
    {
      key: "role",
      label: "Tu rol",
      value: roleName === "admin" ? "Administrador" : "Cajero",
      hint: "Sesión verificada",
      icon: "ri-shield-user-line",
      tone: "secondary",
    },
  ];

  const toneClass: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-800",
    secondary: "bg-secondary-100 text-secondary-800",
  };

  return (
    <div className="flex flex-col gap-6">
      <section className="animate-fade-in rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary-600">
              {today}
            </p>
            <h1 className="font-heading mt-1.5 text-2xl font-bold text-foreground-950 md:text-3xl">
              {greeting()}, {profile?.full_name}
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-500">
              Bienvenido al panel administrativo de Sistema RKC. Administra sucursales, usuarios y
              permisos; el punto de venta está listo para operar.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/pos"
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-store-2-line text-base"></i>
              Abrir POS
            </Link>
            <Link
              to="/admin/products"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-900 transition-colors hover:bg-background-100"
            >
              <i className="ri-shopping-bag-3-line text-base"></i>
              Nuevo producto
            </Link>
            <Link
              to="/admin/inventory"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-900 transition-colors hover:bg-background-100"
            >
              <i className="ri-stack-line text-base"></i>
              Inventario
            </Link>
            <Link
              to="/admin/invoices"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-900 transition-colors hover:bg-background-100"
            >
              <i className="ri-receipt-line text-base"></i>
              Facturación
            </Link>
            <Link
              to="/admin/cash"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-900 transition-colors hover:bg-background-100"
            >
              <i className="ri-cash-line text-base"></i>
              Caja
            </Link>
            <Link
              to="/admin/profits"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-900 transition-colors hover:bg-background-100"
            >
              <i className="ri-line-chart-line text-base"></i>
              Ganancias
            </Link>
          </div>
        </div>
      </section>

      {failed ? (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-2">
            <i className="ri-error-warning-line text-base"></i>
            No se pudieron cargar los datos del panel.
          </span>
          <button
            type="button"
            onClick={load}
            className="whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-primary-700"
          >
            Reintentar
          </button>
        </div>
      ) : null}

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <div
            key={card.key}
            className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5"
          >
            <span
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${toneClass[card.tone]}`}
            >
              <i className={`${card.icon} text-xl`}></i>
            </span>
            <div className="min-w-0">
              <p className="text-xs font-medium text-foreground-500">{card.label}</p>
              <p className="font-heading mt-1 truncate text-xl font-bold text-foreground-950">
                {loading ? "—" : card.value}
              </p>
              <p className="mt-0.5 truncate text-xs text-foreground-400">{card.hint}</p>
            </div>
          </div>
        ))}
      </section>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <section className="xl:col-span-2 rounded-lg border border-background-200 bg-background-50 p-5">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-heading text-base font-semibold text-foreground-950">
              Actividad reciente
            </h2>
            <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-semibold text-secondary-800">
              Auditoría
            </span>
          </div>

          {audit.length === 0 ? (
            <div className="mt-4 flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-background-300 bg-background-100 px-4 py-10 text-center">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-history-line text-xl"></i>
              </span>
              <p className="text-sm font-medium text-foreground-700">Sin movimientos todavía</p>
              <p className="max-w-sm text-xs text-foreground-500">
                Aquí verás cada creación, edición o cambio importante realizado en el sistema.
              </p>
            </div>
          ) : (
            <ul className="mt-4 flex flex-col divide-y divide-background-200">
              {audit.map((row) => (
                <li key={row.id} className="flex items-center gap-3 py-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-600">
                    <i className="ri-edit-2-line text-base"></i>
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-foreground-900">
                      <span className="font-semibold">{row.user_name || "Sistema"}</span>{" "}
                      {ACTION_LABELS[row.action] || row.action}{" "}
                      {ENTITY_LABELS[row.entity] || row.entity}
                    </p>
                    <p className="text-xs text-foreground-400">
                      {new Date(row.created_at).toLocaleString("es-DO", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-lg border border-background-200 bg-background-100 p-5">
          <h2 className="font-heading text-base font-semibold text-foreground-950">
            Módulos activos
          </h2>
          <p className="mt-1 text-xs text-foreground-500">
            Estado de las fases del proyecto.
          </p>
          <ul className="mt-4 flex flex-col gap-3">
            {[
              { label: "Base de datos y seguridad (RLS)", phase: "Fase 1", done: true },
              { label: "Acceso y usuarios con permisos", phase: "Fase 2", done: true },
              { label: "Configuración del sistema", phase: "Fase 3", done: true },
              { label: "Sucursales", phase: "Fase 4", done: true },
              { label: "Productos y catálogo", phase: "Fase 5", done: true },
              { label: "Inventario y movimientos", phase: "Fase 6", done: true },
              { label: "Clientes y mayoristas", phase: "Fase 7", done: true },
              { label: "Taller, vehículos y servicios", phase: "Fase 8", done: true },
              { label: "Ofertas y descuentos", phase: "Fase 9", done: true },
              { label: "POS funcional", phase: "Fase 10", done: true },
              { label: "Facturación y plantillas", phase: "Fase 11", done: true },
              { label: "Cuadre de caja", phase: "Fase 12", done: true },
              { label: "Ganancias y reportes", phase: "Fase 13", done: true },
            ].map((item) => (
              <li key={item.label} className="flex items-start gap-3">
                <span
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                    item.done ? "bg-accent-500 text-background-50" : "bg-background-300 text-foreground-500"
                  }`}
                >
                  <i className={`${item.done ? "ri-check-line" : "ri-time-line"} text-xs`}></i>
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground-900">{item.label}</p>
                  <p className="text-xs text-foreground-400">{item.phase}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}